import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const VIEW_MARGIN = 8;

function clampMenuPlacement(wrapEl, menuEl) {
  if (!wrapEl || !menuEl) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const br = wrapEl.getBoundingClientRect();
  const maxW = vw - 2 * VIEW_MARGIN;

  const w = Math.min(menuEl.offsetWidth, maxW);
  const contentH = menuEl.scrollHeight;

  let left = br.left;
  if (left + w > vw - VIEW_MARGIN) {
    left = vw - VIEW_MARGIN - w;
  }
  if (left < VIEW_MARGIN) {
    left = VIEW_MARGIN;
  }

  const gap = 2;
  const spaceBelow = vh - VIEW_MARGIN - br.bottom - gap;
  const spaceAbove = br.top - VIEW_MARGIN - gap;

  let top;
  /** Якщо задано — меню з overflow-y: auto, щоб усі пункти були доступні. */
  let maxHeight;

  if (contentH <= spaceBelow) {
    top = br.bottom + gap;
  } else if (contentH <= spaceAbove) {
    top = br.top - contentH - gap;
  } else if (spaceBelow >= spaceAbove) {
    top = br.bottom + gap;
    maxHeight = Math.max(120, spaceBelow);
  } else {
    maxHeight = Math.max(120, spaceAbove);
    top = br.top - maxHeight - gap;
  }

  if (top < VIEW_MARGIN) {
    top = VIEW_MARGIN;
  }
  if (maxHeight != null) {
    const bottom = top + maxHeight;
    if (bottom > vh - VIEW_MARGIN) {
      maxHeight = Math.max(120, vh - VIEW_MARGIN - top);
    }
  }

  return { top, left, maxWidth: maxW, width: w, maxHeight };
}

/**
 * Вигляд як бадж; по кліку — меню; довгі списки скроляться в межах екрана (fixed + clamp).
 */
const IncomingStatusBadgePicker = ({
  value,
  options,
  labelOf,
  badgeClassOf,
  onPick,
  disabled,
  title
}) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    const wrap = wrapRef.current;
    const menu = menuRef.current;
    if (!wrap || !menu) return;

    const run = () => {
      const next = clampMenuPlacement(wrap, menu);
      setPlacement(next);
    };

    run();
    window.addEventListener('scroll', run, true);
    window.addEventListener('resize', run);
    return () => {
      window.removeEventListener('scroll', run, true);
      window.removeEventListener('resize', run);
    };
  }, [open, value, (options || []).join('\0')]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const v = value == null ? '' : String(value);
  const busy = Boolean(disabled);

  const handlePick = (next) => {
    if (busy) return;
    if (next !== v) onPick(next);
    setOpen(false);
  };

  const maxWInit = typeof window !== 'undefined' ? window.innerWidth - 2 * VIEW_MARGIN : 360;

  return (
    <div ref={wrapRef} className='position-relative d-inline-block text-left'>
      <button
        type='button'
        className={`${badgeClassOf(v)} border-0`}
        style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
        disabled={busy}
        title={title || 'Натисніть, щоб змінити статус'}
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (!busy) setOpen((o) => !o);
        }}
      >
        {labelOf(v)}
        <span className='sr-only'>, змінити статус</span>
      </button>
      {open && !busy ? (
        <ul
          ref={menuRef}
          className='list-unstyled dropdown-menu show shadow-sm border rounded mb-0 py-1 bg-white'
          role='listbox'
          style={{
            position: 'fixed',
            top: placement ? placement.top : -9999,
            left: placement ? placement.left : 0,
            maxWidth: placement ? placement.maxWidth : maxWInit,
            width: placement ? Math.min(placement.width, placement.maxWidth) : undefined,
            maxHeight: placement?.maxHeight != null ? placement.maxHeight : undefined,
            overflowY: placement?.maxHeight != null ? 'auto' : undefined,
            zIndex: 2000,
            marginTop: 0,
            visibility: placement ? 'visible' : 'hidden',
            pointerEvents: placement ? 'auto' : 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {(options || []).map((opt) => (
            <li key={opt} role='presentation'>
              <button
                type='button'
                role='option'
                aria-selected={opt === v}
                className={`dropdown-item py-2 text-left w-100 border-0 ${opt === v ? 'active font-weight-bold' : 'bg-transparent'}`}
                style={{
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.35
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(opt)}
              >
                {labelOf(opt)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default IncomingStatusBadgePicker;
